import styled, { css } from 'styled-components'

import { breakpoint } from 'utils'
import { BannerPageWrapper } from 'components/Banner'

/**
 * TYPES
 */
interface ContainerProps {
  isSidebarRightOpen?: boolean
  isSidebarLeftOpen?: boolean
  darkBackground?: boolean
}

interface LeftSidebarContainerProps {
  isSidebarLeftOpen?: boolean
}

interface RightSidebarContainerProps {
  isOpen?: boolean
  darkBackground?: boolean
}

/**
 * COMPONENTS
 */
const Container = styled.div<ContainerProps>(
  ({ theme, darkBackground, isSidebarLeftOpen }) => css`
    position: relative;

    display: flex;
    flex: 1 0 auto;
    flex-direction: column;

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    /* Prevent horizontal scrolling when the sidebar is opened */
    overflow-x: ${isSidebarLeftOpen ? 'hidden' : undefined};

    background-color: ${darkBackground
      ? theme.colors.background.light.secondary
      : theme.colors.background.light.base};

    ${breakpoint('sm')`
      margin-right: ${({ isSidebarRightOpen }: ContainerProps) =>
        isSidebarRightOpen && `${theme.sidebar.rightWidth}px`};
    `};
  `
)

const RightSidebarContainer = styled.aside<RightSidebarContainerProps>(
  ({ theme, darkBackground, isOpen }) => css`
    width: 100%;

    position: fixed;
    top: ${theme.header.height}px;
    right: 0;
    bottom: 0;

    display: flex;

    background-color: ${!darkBackground
      ? theme.colors.background.light.secondary
      : theme.colors.background.light.base};
    border-left: 1px solid ${theme.colors.alto};
    border-top-width: 0;
    border-bottom-width: 0;

    /* Force hardware acceleration for CSS transitions */
    will-change: transform;
    transform: ${isOpen ? 'translateX(0)' : 'translateX(100%)'};

    ${breakpoint('sm')`
      width: ${theme.sidebar.rightWidth}px;

      opacity: 0;

      transition-timing-function: ease-in-out;
      transition-duration: 0.3s;
      transition-property: transform, opacity;

      opacity: ${({ isOpen }: RightSidebarContainerProps) => isOpen && 1};
    `};
  `
)

const SidebarLeftContainer = styled.aside<LeftSidebarContainerProps>(
  ({ theme, isSidebarLeftOpen }) => css`
    width: ${theme.sidebar.leftWidth}px;

    position: absolute;
    top: 0;
    bottom: 0;
    z-index: ${theme.zIndex[3]}; /* bigger z-index than header */

    display: flex;

    background-color: ${theme.colors.background.light.primary};
    border-right: 1px solid ${theme.colors.alto};
    border-top-width: 0;
    border-bottom-width: 0;

    box-shadow: ${theme.shadows.boxShadow.elevated};

    will-change: transform; /* Force hardware acceleration */
    transform: ${isSidebarLeftOpen ? 'translateX(0)' : 'translateX(-100%)'};

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    opacity: ${({ isSidebarLeftOpen }: LeftSidebarContainerProps) =>
      isSidebarLeftOpen ? 1 : 0};
  `
)

const BannerWrapper = styled(BannerPageWrapper)`
  margin: 0 16px;

  ${breakpoint('sm')`
    margin: 0 40px;
  `}
`

export { Container, RightSidebarContainer, SidebarLeftContainer, BannerWrapper }
